import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useMedia } from '../../contexts/MediaContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import {
  Play, Square, Video, Mic, MicOff, Camera,
  Loader2, RefreshCw, Maximize2, Minimize2, Volume2, VolumeX,
  Radio, X, Search, ChevronRight,
} from 'lucide-react';
import NativeWebCodecsPlayer from './NativeWebCodecsPlayer';
import NativeLivePlayer from './NativeLivePlayer';
import { createPcmPlayerNode } from '../../utils/pcmPlayerWorklet';
import LiveStreamUnsupportedNotice from './LiveStreamUnsupportedNotice';
import {
  chooseLivePlayerMode,
  LIVE_PLAYER_WEBCODECS,
  LIVE_PLAYER_MSE,
} from '../../utils/liveStreamSupport';
import { getCameraRecordingState, getConfiguredCameraCount } from '../../utils/cameraRecordingState';
import { deviceDisplayName } from '../../utils/deviceName';

// Lazy-loaded to avoid circular import with LiveTrackingVideoModal (which imports
// LiveVideoDeviceActionCard/useIntercom/VideoTile from this file).
const LiveTrackingVideoModal = React.lazy(() =>
  import('../live-tracking/LiveTrackingVideoModal').then((m) => ({ default: m.LiveTrackingVideoModal }))
);

// ══════════════════════════════════════════════════════════════
// Video Tile — single channel player with mute/fullscreen
// ══════════════════════════════════════════════════════════════
export const VideoTile = ({ stream, onStop, channelLabel, timeLeftMs, onExtend, alwaysOn, isStopping, deviceLabel = null }) => {
  const playerRef = useRef(null);
  const tileFrameRef = useRef(null);
  const [status, setStatus] = useState('connecting');
  const [isMuted, setIsMuted] = useState(true);
  const [hasAudio, setHasAudio] = useState(null); // null = unknown, true/false after detection
  const [isFullscreen, setIsFullscreen] = useState(false);
  // iter267 — pick the live player based on browser/device capability:
  //   webcodecs → NativeWebCodecsPlayer (iOS 16.4+, modern desktop)
  //   mse       → NativeLivePlayer (JMuxer/MSE — iPad Safari < 16.4)
  //   unsupported → friendly notice (iPhone Safari < 16.4: no WebCodecs, no MSE)
  const [playerMode, setPlayerMode] = useState(() => chooseLivePlayerMode());
  const { api } = useAuth();

  // Native WebCodecs path — backend listener receives A9A TCP media packets
  // on :34006, reassembles complete protocol frames, and pushes H.264 Annex-B
  // frames over this channel-scoped WebSocket.
  const createWsUrl = useCallback(async () => {
    const apiBase = process.env.REACT_APP_BACKEND_URL || '';
    const wsBase = apiBase.replace(/^http/, 'ws');
    const imei = stream.device_imei || '';
    const channel = stream.camera_id || stream.channel;
    if (!imei || !channel) return null;
    const res = await api.post('/media/native-live/token', {
      device_id: stream.device_id,
      imei,
      channel: Number(channel),
      stream_type: stream.stream_type || 'sub',
    });
    const streamToken = res.data?.stream_token;
    if (!streamToken) throw new Error('Stream token unavailable');
    return `${wsBase}/api/media/native-live/${imei}/${channel}/stream?stream_token=${encodeURIComponent(streamToken)}`;
  }, [api, stream.device_id, stream.device_imei, stream.camera_id, stream.channel, stream.stream_type]);

  const toggleMute = () => {
    const next = !isMuted;
    playerRef.current?.setMuted(next);
    setIsMuted(next);
    if (!next && hasAudio === false) {
      toast.warning('No audio track in this stream — check device audio settings');
    }
  };

  useEffect(() => {
    const syncFullscreenState = () => {
      const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
      setIsFullscreen(fullscreenElement === tileFrameRef.current);
    };
    document.addEventListener('fullscreenchange', syncFullscreenState);
    document.addEventListener('webkitfullscreenchange', syncFullscreenState);
    return () => {
      document.removeEventListener('fullscreenchange', syncFullscreenState);
      document.removeEventListener('webkitfullscreenchange', syncFullscreenState);
    };
  }, []);

  const toggleFullscreen = async () => {
    const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
    try {
      if (fullscreenElement) {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) await document.webkitExitFullscreen();
        return;
      }
      const target = tileFrameRef.current;
      if (!target) return;
      if (target.requestFullscreen) await target.requestFullscreen();
      else if (target.webkitRequestFullscreen) await target.webkitRequestFullscreen();
    } catch (_) {
      playerRef.current?.goFullscreen();
    }
  };

  // Format countdown mm:ss
  const fmt = (ms) => {
    if (ms == null) return null;
    const s = Math.max(0, Math.round(ms / 1000));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };
  const remaining = fmt(timeLeftMs);
  const lowTime = timeLeftMs != null && timeLeftMs < 60_000;

  return (
    <Card className="overflow-hidden" data-testid={`tile-${stream.id}`}>
      <div
        ref={tileFrameRef}
        className={`${isFullscreen ? 'w-screen h-screen aspect-auto' : 'aspect-video'} bg-black relative group overflow-hidden`}
        data-testid={`live-video-frame-${stream.id}`}
      >
        <Badge className="absolute top-2 left-2 bg-red-500 text-white z-20 pointer-events-none">
          <span className={`w-2 h-2 rounded-full mr-1.5 inline-block ${status === 'playing' ? 'bg-white animate-pulse' : 'bg-white/50'}`} />
          {status === 'playing' ? 'LIVE' : 'CONNECTING'}
        </Badge>
        <Badge
          className="absolute top-2 right-2 bg-blue-600 text-white text-xs z-20 pointer-events-none max-w-[50%] truncate"
          title={channelLabel || `CH${stream.camera_id}`}
        >
          {channelLabel || `CH${stream.camera_id}`}
        </Badge>
        <Badge className="absolute top-10 right-2 bg-slate-700 text-white text-xs z-20 pointer-events-none">
          {stream.stream_type === 'sub' ? 'Sub' : 'Main'}
        </Badge>
        {/* Auto-timeout countdown / Always-on indicator */}
        {alwaysOn ? (
          <Badge className="absolute top-10 left-2 bg-purple-600 text-white text-[10px] z-20 pointer-events-none">
            ∞ Always On
          </Badge>
        ) : remaining && (
          <Badge
            className={`absolute top-10 left-2 text-white text-[10px] z-20 pointer-events-none
              ${lowTime ? 'bg-amber-500 animate-pulse' : 'bg-slate-800/80'}`}
            data-testid={`countdown-${stream.id}`}
          >
            ⏱ {remaining}
          </Badge>
        )}

        {playerMode === LIVE_PLAYER_WEBCODECS && (
          <NativeWebCodecsPlayer
            ref={playerRef}
            wsUrlFactory={createWsUrl}
            onStatusChange={setStatus}
            onAudioDetected={setHasAudio}
            label={`${deviceLabel || stream.device_name || ''} CH${stream.channel}`}
          />
        )}
        {playerMode === LIVE_PLAYER_MSE && (
          <NativeLivePlayer
            ref={playerRef}
            wsUrlFactory={createWsUrl}
            onStatusChange={setStatus}
            label={`${deviceLabel || stream.device_name || ''} CH${stream.channel}`}
          />
        )}
        {playerMode !== LIVE_PLAYER_WEBCODECS && playerMode !== LIVE_PLAYER_MSE && (
          <LiveStreamUnsupportedNotice
            label={`${deviceLabel || stream.device_name || ''} CH${stream.channel}`}
            onRetry={() => setPlayerMode(LIVE_PLAYER_WEBCODECS)}
          />
        )}

        {/* Low-time warning banner + Extend button */}
        {lowTime && !alwaysOn && (
          <div className="absolute bottom-12 left-2 right-2 z-20 flex items-center justify-between bg-amber-500/95 text-white text-xs rounded px-2 py-1.5 shadow-lg">
            <span>Auto-stop in {remaining}</span>
            <button
              onClick={(e) => { e.stopPropagation(); onExtend?.(); }}
              className="bg-white text-amber-700 font-semibold px-2 py-0.5 rounded hover:bg-amber-50"
              data-testid={`extend-${stream.id}`}
            >
              Extend
            </button>
          </div>
        )}

        {/* Hover controls: mute / fullscreen / stop only */}
        <div className={`absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/90 to-transparent
                        ${isFullscreen ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity flex items-center justify-between gap-1 z-30`}>
          <div className="text-white text-xs font-medium truncate pl-1" data-testid={`tile-device-label-${stream.id}`}>
            {(deviceLabel || stream.device_name) || ''} · {channelLabel || `CH${stream.camera_id}`}
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" className="text-white hover:bg-white/20 h-7 w-7 p-0"
              onClick={toggleMute} data-testid={`mute-${stream.id}`}
              title={isMuted ? 'Unmute video audio' : 'Mute video audio'}>
              {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </Button>
            <Button size="sm" variant="ghost" className="text-white hover:bg-white/20 h-7 w-7 p-0"
              onClick={toggleFullscreen} data-testid={`fs-${stream.id}`}
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </Button>
            <Button size="sm" className="ml-2 gap-2 bg-red-600 hover:bg-red-700 text-white h-8"
              onClick={() => onStop(stream.device_id, stream.camera_id)}
              disabled={isStopping}
              data-testid={`stop-${stream.id}`} title="Stop stream">
              {isStopping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Square className="w-4 h-4" />} Stop
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

// ══════════════════════════════════════════════════════════════
// Intercom session manager — captures mic + WHIP publishes + WHEP listens
// ══════════════════════════════════════════════════════════════
const TARGET_INTERCOM_SAMPLE_RATE = 8000;
const INTERCOM_MIC_BOOST_GAIN = 2.0;
const DEFAULT_INTERCOM_MODE = 'open';
const INTERCOM_MODE_STORAGE_PREFIX = 'intercomMode:';

const readIntercomModeForDevice = (deviceId) => {
  if (!deviceId) return DEFAULT_INTERCOM_MODE;
  try {
    return localStorage.getItem(`${INTERCOM_MODE_STORAGE_PREFIX}${deviceId}`) || DEFAULT_INTERCOM_MODE;
  } catch (_) {
    return DEFAULT_INTERCOM_MODE;
  }
};

const downsampleToPcm16 = (input, inputRate) => {
  if (!input?.length || !inputRate) return new ArrayBuffer(0);
  const ratio = inputRate / TARGET_INTERCOM_SAMPLE_RATE;
  const outputLength = Math.max(1, Math.floor(input.length / ratio));
  const output = new Int16Array(outputLength);
  for (let i = 0; i < outputLength; i += 1) {
    const start = Math.floor(i * ratio);
    const end = Math.min(input.length, Math.floor((i + 1) * ratio));
    let sum = 0;
    let count = 0;
    for (let j = start; j < end; j += 1) {
      sum += input[j];
      count += 1;
    }
    const sample = Math.max(-1, Math.min(1, count ? sum / count : input[start] || 0));
    output[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return output.buffer;
};

const pcm16ToFloat32 = (payload) => {
  const view = new DataView(payload);
  const samples = Math.floor(payload.byteLength / 2);
  const out = new Float32Array(samples);
  for (let i = 0; i < samples; i += 1) {
    out[i] = view.getInt16(i * 2, true) / 32768;
  }
  return out;
};

export const useIntercom = () => {
  const [activeDeviceId, setActiveDeviceId] = useState(null);
  const [mode, setModeState] = useState(DEFAULT_INTERCOM_MODE);
  const [modeByDevice, setModeByDevice] = useState({});
  const [isTransmitting, setIsTransmitting] = useState(false);
  const modeRef = useRef(mode);
  const activeDeviceIdRef = useRef(null);
  const transmittingRef = useRef(false);
  const pcPublishRef = useRef(null);
  const pcListenRef = useRef(null);
  const micStreamRef = useRef(null);
  const downAudioRef = useRef(null); // hidden <audio> for driver → operator
  const listenRetryRef = useRef(null);
  const listenActiveRef = useRef(false);
  const nativeWsRef = useRef(null);
  const nativeAudioCtxRef = useRef(null);
  const nativeSourceRef = useRef(null);
  const nativeProcessorRef = useRef(null);
  const nativeMicGainRef = useRef(null);
  const nativeLimiterRef = useRef(null);
  const nativeWorkletRef = useRef(null);

  const getMode = useCallback((deviceId) => {
    if (!deviceId) return DEFAULT_INTERCOM_MODE;
    return modeByDevice[deviceId] || readIntercomModeForDevice(deviceId);
  }, [modeByDevice]);

  // Enable/disable microphone track without tearing down the native intercom session.
  const setTransmitting = useCallback((on) => {
    transmittingRef.current = on;
    if (micStreamRef.current) {
      micStreamRef.current.getAudioTracks().forEach(t => { t.enabled = on; });
    }
    setIsTransmitting(on);
  }, []);

  const setDeviceMode = useCallback((deviceId, m) => {
    if (!deviceId) return;
    try {
      localStorage.setItem(`${INTERCOM_MODE_STORAGE_PREFIX}${deviceId}`, m);
      localStorage.removeItem('intercomMode');
    } catch (_) { /* ignore storage failures */ }
    setModeByDevice(prev => ({ ...prev, [deviceId]: m }));
    if (activeDeviceIdRef.current === deviceId) {
      modeRef.current = m;
      setModeState(m);
      setTransmitting(m !== 'ptt');
    }
  }, [setTransmitting]);

  const stop = useCallback(() => {
    listenActiveRef.current = false;
    clearTimeout(listenRetryRef.current);
    try { pcPublishRef.current?.close(); } catch (_) {}
    try { pcListenRef.current?.close(); } catch (_) {}
    try { nativeWsRef.current?.close(); } catch (_) {}
    try { nativeProcessorRef.current?.disconnect(); } catch (_) {}
    try { nativeSourceRef.current?.disconnect(); } catch (_) {}
    try { nativeMicGainRef.current?.disconnect(); } catch (_) {}
    try { nativeLimiterRef.current?.disconnect(); } catch (_) {}
    try { nativeWorkletRef.current?.port.postMessage({ type: 'reset' }); } catch (_) {}
    try { nativeWorkletRef.current?.disconnect(); } catch (_) {}
    try { nativeAudioCtxRef.current?.close(); } catch (_) {}
    pcPublishRef.current = null;
    pcListenRef.current = null;
    nativeWsRef.current = null;
    nativeProcessorRef.current = null;
    nativeSourceRef.current = null;
    nativeMicGainRef.current = null;
    nativeLimiterRef.current = null;
    nativeWorkletRef.current = null;
    nativeAudioCtxRef.current = null;
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(t => t.stop());
      micStreamRef.current = null;
    }
    if (downAudioRef.current) {
      downAudioRef.current.srcObject = null;
    }
    transmittingRef.current = false;
    activeDeviceIdRef.current = null;
    setIsTransmitting(false);
    setActiveDeviceId(null);
  }, []);

  const startNativeCommand = useCallback((deviceId) => {
    const deviceMode = readIntercomModeForDevice(deviceId);
    modeRef.current = deviceMode;
    setModeState(deviceMode);
    setModeByDevice(prev => ({ ...prev, [deviceId]: deviceMode }));
    activeDeviceIdRef.current = deviceId;
    transmittingRef.current = false;
    setIsTransmitting(false);
    setActiveDeviceId(deviceId);
  }, []);

  const playInboundPcm = useCallback((arrayBuffer) => {
    const context = nativeAudioCtxRef.current;
    const worklet = nativeWorkletRef.current;
    if (!context || context.state === 'closed' || !worklet || !arrayBuffer?.byteLength) return;
    const pcm = pcm16ToFloat32(arrayBuffer);
    if (!pcm.length) return;
    worklet.port.postMessage(
      { type: 'push', samples: pcm, rate: TARGET_INTERCOM_SAMPLE_RATE },
      [pcm.buffer],
    );
  }, []);

  const startNativeAudio = useCallback(async (deviceId, wsUrl) => {
    let micStream;
    try {
      micStream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: false,
      });
      micStreamRef.current = micStream;
    } catch (e) {
      toast.error('Microphone access denied. Please grant mic permission for intercom.');
      throw e;
    }

    const AudioCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtor) {
      toast.error('This browser does not support WebAudio intercom.');
      throw new Error('WebAudio unavailable');
    }

    const context = new AudioCtor();
    nativeAudioCtxRef.current = context;
    if (context.state === 'suspended') {
      try { await context.resume(); } catch (_) { /* user gesture may be required */ }
    }
    try {
      const workletNode = await createPcmPlayerNode(context);
      if (workletNode) {
        workletNode.connect(context.destination);
        nativeWorkletRef.current = workletNode;
      }
    } catch (_) { /* worklet unavailable — inbound audio silent, mic still works */ }
    const source = context.createMediaStreamSource(micStream);
    // Mic boost: browser AGC (above) + fixed gain, tamed by a compressor
    // configured as a soft limiter so shouting doesn't hard-clip into the
    // G.711 encode downstream.
    const micGain = context.createGain();
    micGain.gain.value = INTERCOM_MIC_BOOST_GAIN;
    const limiter = context.createDynamicsCompressor();
    limiter.threshold.value = -6;
    limiter.knee.value = 6;
    limiter.ratio.value = 16;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.25;
    const processor = context.createScriptProcessor(2048, 1, 1);
    const ws = new WebSocket(wsUrl);
    ws.binaryType = 'arraybuffer';
    nativeWsRef.current = ws;
    nativeSourceRef.current = source;
    nativeMicGainRef.current = micGain;
    nativeLimiterRef.current = limiter;
    nativeProcessorRef.current = processor;

    ws.onmessage = async (event) => {
      if (event.data instanceof Blob) {
        playInboundPcm(await event.data.arrayBuffer());
      } else if (event.data instanceof ArrayBuffer) {
        playInboundPcm(event.data);
      }
    };
    ws.onerror = () => toast.error('Intercom audio connection error');
    ws.onclose = () => setIsTransmitting(false);

    await new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('Intercom audio connection timeout')), 15000);
      ws.onopen = () => { clearTimeout(timer); resolve(); };
      ws.onerror = () => { clearTimeout(timer); reject(new Error('Intercom audio connection failed')); };
    });

    processor.onaudioprocess = (event) => {
      if (ws.readyState !== WebSocket.OPEN) return;
      if (modeRef.current === 'ptt' && !transmittingRef.current) return;
      const input = event.inputBuffer.getChannelData(0);
      const pcm16 = downsampleToPcm16(input, context.sampleRate);
      if (pcm16.byteLength > 0) ws.send(pcm16);
    };
    source.connect(micGain);
    micGain.connect(limiter);
    limiter.connect(processor);
    processor.connect(context.destination);
    setActiveDeviceId(deviceId);
    setTransmitting(modeRef.current !== 'ptt');
  }, [playInboundPcm, setTransmitting]);

  const start = useCallback(async (deviceId, whepUrl, whipUrl) => {
    // 1. Get mic
    let micStream;
    try {
      micStream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: false,
      });
      micStreamRef.current = micStream;
    } catch (e) {
      toast.error('Microphone access denied. Please grant mic permission for intercom.');
      throw e;
    }

    // 2. WHIP publish mic → MediaMTX (MediaMTX relays RTMP → device)
    //    In PTT mode, mic track starts DISABLED — user holds Space to transmit.
    try {
      const pcPub = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
      pcPublishRef.current = pcPub;
      const startMuted = mode === 'ptt';
      micStream.getAudioTracks().forEach(track => {
        track.enabled = !startMuted;
        pcPub.addTrack(track, micStream);
      });
      setIsTransmitting(!startMuted);
      const offerPub = await pcPub.createOffer();
      await pcPub.setLocalDescription(offerPub);
      await new Promise(resolve => {
        if (pcPub.iceGatheringState === 'complete') { resolve(); return; }
        const check = () => { if (pcPub.iceGatheringState === 'complete') { pcPub.removeEventListener('icegatheringstatechange', check); resolve(); } };
        pcPub.addEventListener('icegatheringstatechange', check);
        setTimeout(resolve, 3000);
      });
      const resPub = await fetch(whipUrl, {
        method: 'POST', headers: { 'Content-Type': 'application/sdp' },
        body: pcPub.localDescription.sdp,
      });
      if (!resPub.ok) throw new Error(`WHIP failed: ${resPub.status}`);
      const answerPub = await resPub.text();
      await pcPub.setRemoteDescription({ type: 'answer', sdp: answerPub });
    } catch (e) {
      toast.error('Failed to start mic upstream: ' + e.message);
      stop();
      throw e;
    }

    // 3. WHEP listen: driver-cabin audio (device → MediaMTX → browser)
    // Retry every 2s for up to 60s because the device takes a few seconds
    // to respond to AB2 and FFmpeg takes ~1s to warm up transcoding.
    listenActiveRef.current = true;
    const attemptListen = async (attempt = 0) => {
      if (!listenActiveRef.current) return;
      try {
        // Close previous attempt's PC if any
        try { pcListenRef.current?.close(); } catch (_) {}

        const pcLis = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
        pcListenRef.current = pcLis;
        pcLis.addTransceiver('audio', { direction: 'recvonly' });
        pcLis.ontrack = (event) => {
          if (downAudioRef.current && event.streams[0]) {
            downAudioRef.current.srcObject = event.streams[0];
            downAudioRef.current.play().catch(() => {});
          }
        };
        const offerLis = await pcLis.createOffer();
        await pcLis.setLocalDescription(offerLis);
        await new Promise(resolve => {
          if (pcLis.iceGatheringState === 'complete') { resolve(); return; }
          const check = () => {
            if (pcLis.iceGatheringState === 'complete') {
              pcLis.removeEventListener('icegatheringstatechange', check);
              resolve();
            }
          };
          pcLis.addEventListener('icegatheringstatechange', check);
          setTimeout(resolve, 3000);
        });

        const r = await fetch(whepUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/sdp' },
          body: pcLis.localDescription.sdp,
        });

        if (r.ok) {
          const ans = await r.text();
          await pcLis.setRemoteDescription({ type: 'answer', sdp: ans });
          // Connected — no more retries
          return;
        }

        // Stream not ready yet → retry
        if (listenActiveRef.current && attempt < 30) {
          listenRetryRef.current = setTimeout(() => attemptListen(attempt + 1), 2000);
        }
      } catch (_) {
        if (listenActiveRef.current && attempt < 30) {
          listenRetryRef.current = setTimeout(() => attemptListen(attempt + 1), 2000);
        }
      }
    };
    attemptListen();

    setActiveDeviceId(deviceId);
  }, [stop, mode]);

  // Global Spacebar keyboard listener for PTT — only active when intercom is live in PTT mode
  useEffect(() => {
    if (!activeDeviceId || mode !== 'ptt') return;

    const isTypingTarget = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
    };

    const onKeyDown = (e) => {
      if (e.code !== 'Space') return;
      if (e.repeat) return;
      if (!document.hasFocus() || document.visibilityState !== 'visible') return;
      if (isTypingTarget(e.target)) return;
      e.preventDefault();
      setTransmitting(true);
    };
    const onKeyUp = (e) => {
      if (e.code !== 'Space') return;
      if (isTypingTarget(e.target)) return;
      e.preventDefault();
      setTransmitting(false);
    };
    const stopPtt = () => setTransmitting(false);

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', stopPtt);
    document.addEventListener('visibilitychange', stopPtt);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', stopPtt);
      document.removeEventListener('visibilitychange', stopPtt);
    };
  }, [activeDeviceId, mode, setTransmitting]);

  useEffect(() => () => stop(), [stop]);

  return { activeDeviceId, mode, getMode, setDeviceMode, isTransmitting, setTransmitting, start, startNativeCommand, startNativeAudio, stop, downAudioRef };
};

// ══════════════════════════════════════════════════════════════
// Device Row — in left sidebar
// ══════════════════════════════════════════════════════════════
export const LiveVideoDeviceActionCard = ({ device, activeStreams, startingKey, onPlay, onStop,
                    stoppingKeys,
                    intercomActive, onIntercomStart, onIntercomStop, isSelected, onSelect,
                    intercomMode, onIntercomModeChange, isTransmitting, onPttPress, onPttRelease,
                    intercomLocked, streamType, onStreamTypeChange }) => {
  const totalChannels = getConfiguredCameraCount(device);
  const channels = Array.from({ length: totalChannels }, (_, i) => i + 1);
  const cameraRecordingState = getCameraRecordingState(device);
  const intercomDisabled = !intercomActive && (!cameraRecordingState.isGprsOnline || intercomLocked);
  const expanded = isSelected || intercomActive;
  const anyCameraRecording = cameraRecordingState.hasAnyRecording;

  return (
    <div
      className={`rounded-md border-2 transition-all cursor-pointer p-2 space-y-1.5 shadow-sm
        ${isSelected
          ? 'border-[#2563eb] bg-[#eff6ff] dark:bg-[#1f2d46] dark:border-[#3b82f6]'
          : 'border-slate-300 bg-slate-50 hover:border-slate-500 dark:border-slate-600 dark:bg-slate-800/70 dark:hover:border-slate-400'}
        ${activeStreams.length > 0 ? 'ring-1 ring-red-500/60' : ''}`}
      onClick={onSelect}
      data-testid={`device-row-${device.id}`}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        <span
          className={`w-2 h-2 rounded-full shrink-0 ${cameraRecordingState.isGprsOnline ? 'bg-emerald-500' : 'bg-slate-400'} ${anyCameraRecording ? 'ring-2 ring-red-600 dark:ring-red-400 ring-offset-1 ring-offset-slate-50 dark:ring-offset-slate-800' : ''}`}
          data-testid={`device-status-dot-${device.id}`}
          title={anyCameraRecording ? 'Camera recording' : (cameraRecordingState.isGprsOnline ? 'GPRS online' : 'GPRS offline')}
        />
        <h4 className="font-semibold text-xs truncate flex-1 min-w-0 text-[#d97706] dark:text-orange-300" title={deviceDisplayName(device)}>{deviceDisplayName(device)}</h4>
        <div
          className="flex items-center rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 p-0.5 shrink-0"
          data-testid={`device-stream-selector-${device.id}`}
          title="Select stream quality for this device"
          onClick={(e) => e.stopPropagation()}
        >
          {['sub', 'main'].map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => onStreamTypeChange(option)}
              className={`px-1.5 py-0.5 rounded text-[9px] font-semibold transition-colors ${streamType === option
                ? 'bg-[#2563eb] text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-700'}`}
              data-testid={`device-stream-${option}-${device.id}`}
              aria-pressed={streamType === option}
            >
              {option === 'sub' ? 'Sub' : 'Main'}
            </button>
          ))}
        </div>
        {activeStreams.length > 0 && (
          <Badge className="bg-red-600 text-white text-[9px] h-4 px-1.5 shrink-0" data-testid={`device-live-badge-${device.id}`}>
            LIVE
          </Badge>
        )}
      </div>

      {/* Channel play/stop row */}
      <div className="grid grid-cols-[minmax(0,1fr)_2rem] gap-1.5 min-w-0 items-stretch">
        <div className={`grid gap-1.5 min-w-0 ${totalChannels === 1 ? 'grid-cols-1' : 'grid-cols-2'}`} data-testid={`channel-grid-${device.id}`}>
          {channels.map(ch => {
            const isStarting = startingKey === `${device.id}|${ch}`;
            const isStopping = stoppingKeys.has(`${device.id}|${ch}`);
            const chActive = activeStreams.find(s => Number(s.camera_id || s.channel) === Number(ch));
            const channelRecording = cameraRecordingState.isGprsOnline && cameraRecordingState.recordingByChannel[ch] === true;
            const channelUnavailable = !chActive && (!cameraRecordingState.isGprsOnline || !channelRecording);
            const label = (device.channel_names && device.channel_names[String(ch)]) || `CH${ch}`;
            return (
              <Button
                key={ch}
                size="sm"
                variant={chActive ? 'destructive' : 'outline'}
                onClick={(e) => { e.stopPropagation(); chActive ? onStop(device.id, ch) : onPlay(device.id, ch); }}
                disabled={isStarting || isStopping || channelUnavailable}
                className={`text-[10px] h-7 gap-1 px-1.5 min-w-0 justify-center text-center border ${chActive
                  ? 'border-red-700 bg-red-600 hover:bg-red-700 text-white dark:border-red-400 dark:bg-red-700 dark:hover:bg-red-600'
                  : channelUnavailable
                    ? 'border-slate-300 bg-slate-100 text-slate-400 opacity-60 dark:border-slate-600 dark:bg-slate-800/40 dark:text-slate-500'
                  : 'border-emerald-600 bg-emerald-50 hover:bg-emerald-100 text-slate-800 dark:border-emerald-400 dark:bg-emerald-900/40 dark:hover:bg-emerald-800/60 dark:text-slate-100'}`}
                title={!cameraRecordingState.isGprsOnline ? 'Device offline — streaming disabled' : (!channelRecording && !chActive ? `${label} is not recording — streaming disabled` : label)}
                data-testid={`ch${ch}-${device.id}`}
              >
                {isStarting || isStopping ? <Loader2 className="w-3 h-3 animate-spin" /> :
                 chActive ? <><Square className="w-2.5 h-2.5 shrink-0" /> <span className="truncate min-w-0 text-center">{label}</span></> :
                 <><Play className="w-2.5 h-2.5 shrink-0" /> <span className="truncate min-w-0 text-center">{label}</span></>}
              </Button>
            );
          })}
        </div>
        <Button
          size="sm"
          variant={intercomActive ? 'default' : 'outline'}
          onClick={(e) => { e.stopPropagation(); intercomActive ? onIntercomStop(device.id) : onIntercomStart(device); }}
          disabled={intercomDisabled}
          className={`h-full min-h-7 w-8 p-0 shrink-0 border ${intercomActive
            ? 'border-green-700 bg-green-600 hover:bg-green-700 text-white dark:border-green-400 dark:bg-green-700 dark:hover:bg-green-600'
            : 'border-slate-400 bg-white hover:bg-slate-100 text-slate-800 dark:border-slate-400 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-100'} ${intercomActive && !isTransmitting ? 'animate-pulse' : ''}`}
          data-testid={`intercom-${device.id}`}
          title={intercomLocked ? 'Another intercom session is active' : (!cameraRecordingState.isGprsOnline && !intercomActive ? 'Device offline — cannot start intercom' : (intercomActive ? 'Stop Intercom' : 'Intercom'))}
        >
          {intercomActive ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
        </Button>
      </div>

      {expanded && (
        <div className="space-y-1.5 pt-1 border-t border-border/60" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-1 text-[10px] bg-slate-100 dark:bg-slate-800 rounded p-0.5" data-testid={`intercom-mode-selector-${device.id}`}>
            <button
              type="button"
              onClick={() => onIntercomModeChange('open')}
              className={`flex-1 py-1 rounded transition-colors ${intercomMode === 'open' ? 'bg-white dark:bg-slate-700 font-medium shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid={`mode-live-mic-${device.id}`}
              title="Keep microphone open while intercom is active"
            >
              Live Mic
            </button>
            <button
              type="button"
              onClick={() => onIntercomModeChange('ptt')}
              className={`flex-1 py-1 rounded transition-colors ${intercomMode === 'ptt' ? 'bg-white dark:bg-slate-700 font-medium shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid={`mode-ptt-${device.id}`}
              title="Listen continuously; transmit only while holding Space"
            >
              Push-to-Talk
            </button>
          </div>
          <p className="text-[10px] text-muted-foreground truncate" title={device.imei} data-testid={`device-imei-${device.id}`}>
            {device.imei}
          </p>

        {intercomActive && (
          <p className="text-[10px] text-green-600 dark:text-green-400 font-medium" data-testid={`native-intercom-active-${device.id}`}>
            {intercomMode === 'ptt'
              ? (isTransmitting ? 'Mic transmitting' : 'Listening — hold Space to talk')
              : 'Live microphone transmitting'}
          </p>
        )}

        {intercomActive && intercomMode === 'ptt' && (
          <button
            type="button"
            onMouseDown={(e) => { e.stopPropagation(); onPttPress(); }}
            onMouseUp={(e) => { e.stopPropagation(); onPttRelease(); }}
            onMouseLeave={(e) => { e.stopPropagation(); onPttRelease(); }}
            onTouchStart={(e) => { e.stopPropagation(); onPttPress(); }}
            onTouchEnd={(e) => { e.stopPropagation(); onPttRelease(); }}
            className={`w-full h-10 rounded-md flex items-center justify-center gap-1.5 font-semibold text-xs select-none transition-transform
              ${isTransmitting
                ? 'bg-red-500 text-white shadow-lg shadow-red-500/40 scale-[0.98] ring-2 ring-red-400'
                : 'bg-slate-900 text-white hover:bg-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600'}`}
            data-testid={`ptt-btn-${device.id}`}
            title="Hold to transmit mic audio"
          >
            {isTransmitting ? (
              <><Radio className="w-3.5 h-3.5 animate-pulse" /> TRANSMITTING</>
            ) : (
              <><Mic className="w-3.5 h-3.5" /> Hold SPACE or press to talk</>
            )}
          </button>
        )}
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// Live Videos Page — split layout
// ══════════════════════════════════════════════════════════════
export const LiveVideos = () => {
  const { api } = useAuth();
  // Direct Consume from MediaContext — these survive sub-tab switches and page navigation
  const {
    lvSelectedDeviceId: selectedDeviceId,
    setLvSelectedDeviceId: setSelectedDeviceId,
    lvDeviceStatusFilter: deviceStatusFilter,
    setLvDeviceStatusFilter: setDeviceStatusFilter,
  } = useMedia();

  const [devices, setDevices] = useState([]);
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startingKey, setStartingKey] = useState(null);
  const [stoppingKeys, setStoppingKeys] = useState(new Set());
  const [deviceStreamTypes, setDeviceStreamTypes] = useState({});
  const [deviceSearchQuery, setDeviceSearchQuery] = useState('');
  // Mobile-only: tap-to-open device viewer. Reuses the Live Tracking video modal
  // so the mobile experience matches the Live Tracking page (single device,
  // stacked channel tiles, integrated intercom).
  const [mobileSelectedDevice, setMobileSelectedDevice] = useState(null);
  const intercom = useIntercom();

  // Auto-timeout state — per-stream deadline timestamps (ms since epoch)
  const [streamDeadlines, setStreamDeadlines] = useState({}); // { "<deviceId>-<ch>": deadlineMs }
  const [nowTick, setNowTick] = useState(Date.now());
  const [limits, setLimits] = useState({
    max_stream_duration_minutes: 3,
    stream_inactivity_timeout_minutes: 1,
  });
  const lastActivityRef = useRef(Date.now());
  // Re-entrancy guard: per-stream-key while a stop request is in-flight
  const stoppingRef = useRef(new Set());
  const stoppedTombstonesRef = useRef(new Map());
  const ownedStreamKeysRef = useRef(new Set());

  useEffect(() => {
    try {
      const raw = window.sessionStorage.getItem('ensue-native-live-owned-streams');
      ownedStreamKeysRef.current = new Set(raw ? JSON.parse(raw) : []);
    } catch (_) {
      ownedStreamKeysRef.current = new Set();
    }
  }, []);

  const persistOwnedStreamKeys = useCallback(() => {
    try {
      window.sessionStorage.setItem(
        'ensue-native-live-owned-streams',
        JSON.stringify([...ownedStreamKeysRef.current]),
      );
    } catch (_) { /* noop */ }
  }, []);

  const ownershipKeys = useCallback((deviceId, imei, channel) => {
    const keys = [];
    if (deviceId) keys.push(`${deviceId}|${channel}`);
    if (imei) keys.push(`${String(imei).replace(/^0+/, '')}|${channel}`);
    return keys;
  }, []);

  const rememberLocalStream = useCallback((deviceId, imei, channel) => {
    ownershipKeys(deviceId, imei, channel).forEach(key => ownedStreamKeysRef.current.add(key));
    persistOwnedStreamKeys();
  }, [ownershipKeys, persistOwnedStreamKeys]);

  const forgetLocalStream = useCallback((deviceId, imei, channel) => {
    ownershipKeys(deviceId, imei, channel).forEach(key => ownedStreamKeysRef.current.delete(key));
    persistOwnedStreamKeys();
  }, [ownershipKeys, persistOwnedStreamKeys]);

  const ownsLocalStream = useCallback((deviceId, imei, channel) => (
    ownershipKeys(deviceId, imei, channel).some(key => ownedStreamKeysRef.current.has(key))
  ), [ownershipKeys]);

  // Fetch user's streaming limits once
  useEffect(() => {
    api.get('/me/stream-limits')
      .then(r => {
        const d = r.data || {};
        setLimits({
          max_stream_duration_minutes: d.max_stream_duration_minutes ?? 10,
          stream_inactivity_timeout_minutes: d.stream_inactivity_timeout_minutes ?? 2,
        });
      })
      .catch(() => {}); // fallback to defaults
  }, [api]);

  // Track user activity (mouse move, keyboard) + tab visibility
  useEffect(() => {
    const bump = () => { lastActivityRef.current = Date.now(); };
    const onVisibility = () => { if (!document.hidden) bump(); };
    window.addEventListener('mousemove', bump);
    window.addEventListener('keydown', bump);
    window.addEventListener('click', bump);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('mousemove', bump);
      window.removeEventListener('keydown', bump);
      window.removeEventListener('click', bump);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Tick every second to re-render countdowns
  useEffect(() => {
    const i = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);

  const fetchDevices = useCallback(async () => {
    try { const res = await api.get('/media/video-devices'); setDevices(res.data); } catch (e) {}
  }, [api]);

  // iter150 — keep a ref of the latest devices so `fetchStreams` can read
  // them without depending on `devices` (which changes reference on every
  // setDevices and was destabilising the polling interval — a new
  // interval was being mounted on every render, hammering the backend).
  const devicesRef = useRef(devices);
  useEffect(() => { devicesRef.current = devices; }, [devices]);
  const streamsRef = useRef(streams);
  useEffect(() => { streamsRef.current = streams; }, [streams]);
  const getDeviceStreamType = useCallback((deviceId) => deviceStreamTypes[deviceId] || 'sub', [deviceStreamTypes]);
  const updateDeviceStreamType = useCallback((deviceId, nextType) => {
    setDeviceStreamTypes(prev => ({ ...prev, [deviceId]: nextType }));
  }, []);

  const fetchStreams = useCallback(async () => {
    try {
      // iter149 phase4b — native A9A pipeline.  fetchStreams() is
      // ENRICH-ONLY: it never removes a tile the user added by clicking
      // play.  When the listener reports an active session for an
      // existing tile, we update that tile's stats; sessions for IMEIs
      // not yet in `streams` are added (e.g. on initial page load when
      // a device is already streaming from a previous session).
      // Tiles are only removed by stopStream() or the auto-timeout.
      const res = await api.get('/media/native-live/active-sessions');
      const sessions = res.data?.sessions || [];
      const now = Date.now();
      for (const [key, expiresAt] of stoppedTombstonesRef.current.entries()) {
        if (expiresAt <= now) stoppedTombstonesRef.current.delete(key);
      }

      const matchSession = (s, x) =>
        String(s.device_imei).replace(/^0+/, '') === String(x.imei).replace(/^0+/, '')
        && Number(s.channel) === Number(x.channel);

      setStreams(prev => {
        // Update stats on any tile whose IMEI/channel matches a live session.
        const enriched = prev.map(s => {
          const session = sessions.find(x => matchSession(s, x));
          return session
            ? { ...s, frames_in: session.frames_in, bytes_in: session.bytes_in,
                viewer_count: session.viewer_count, stream_type: session.stream_type || s.stream_type,
                _optimistic: false }
            : s;
        });
        // Add sessions that aren't already represented (e.g. page reload
        // while a device was mid-stream from a previous browser tab).
        const currentDevices = devicesRef.current || [];
        for (const session of sessions) {
          if (enriched.some(s => matchSession(s, session))) continue;
          if (session.stream_marked_live === false || session.stream_status === 'stopping') continue;
          const dev = currentDevices.find(d =>
            String(d.imei).replace(/^0+/, '') === String(session.imei).replace(/^0+/, '')
          );
          const deviceId = session.device_id || dev?.id || session.imei;
          const tombstoneKey = `${deviceId}|${session.channel}`;
          if (stoppedTombstonesRef.current.has(tombstoneKey)) continue;
          if (!ownsLocalStream(deviceId, session.imei, session.channel)) continue;
          enriched.push({
            id: `native-${session.imei}-${session.channel}`,
            device_id: deviceId,
            device_imei: String(session.imei).replace(/^0+/, ''),
            // Phase 2C: snapshot Asset/Unit so the VideoTile fallback path
            // still shows the user-facing label if the device record is
            // ever unavailable client-side.
            device_name: (dev ? deviceDisplayName(dev) : null) || session.device_name || `IMEI ${session.imei}`,
            channel: session.channel,
            camera_id: session.channel,      // iter150.2 — alias for legacy UI bindings
            stream_type: session.stream_type || 'sub',
            is_live: true,
            started_at: session.started_at,
            frames_in: session.frames_in,
            bytes_in: session.bytes_in,
            viewer_count: session.viewer_count,
          });
        }
        return enriched;
      });
    } catch (e) { /* swallow — interval will retry */ }
    finally { setLoading(false); }
  }, [api, ownsLocalStream]); // iter150 — `devices` removed from deps; read via devicesRef so the
             // callback identity stays stable across renders.

  useEffect(() => { fetchDevices(); fetchStreams(); const i = setInterval(fetchStreams, 10000); return () => clearInterval(i); }, [fetchDevices, fetchStreams]);

  const deviceById = useCallback((id) => devices.find(d => d.id === id), [devices]);

  const _setDeadline = (deviceId, channel, minutes) => {
    setStreamDeadlines(prev => ({ ...prev, [`${deviceId}|${channel}`]: Date.now() + minutes * 60_000 }));
  };
  const _clearDeadline = (deviceId, channel) => {
    setStreamDeadlines(prev => {
      const next = { ...prev }; delete next[`${deviceId}|${channel}`]; return next;
    });
  };

  const startStream = async (deviceId, channel) => {
    setStartingKey(`${deviceId}|${channel}`);
    stoppedTombstonesRef.current.delete(`${deviceId}|${channel}`);
    const dev = deviceById(deviceId);
    const requestedStreamType = getDeviceStreamType(deviceId);
    try {
      const normaliseImei = (value) => String(value || '').replace(/^0+/, '');
      const activeRes = await api.get('/media/native-live/active-sessions');
      const activeSession = (activeRes.data?.sessions || []).find(session => (
        normaliseImei(session.imei) === normaliseImei(dev?.imei)
        && Number(session.channel) === Number(channel)
        && session.stream_marked_live !== false
        && session.stream_status !== 'stopping'
      ));

      if (activeSession) {
        setSelectedDeviceId(deviceId);
        rememberLocalStream(deviceId, dev?.imei || activeSession.imei, channel);
        const attachedStream = {
          id: `native-${activeSession.imei}-${activeSession.channel}`,
          device_id: deviceId,
          device_imei: normaliseImei(activeSession.imei || dev?.imei),
          // Phase 2C: snapshot Asset/Unit (see fetchStreams above).
          device_name: (dev ? deviceDisplayName(dev) : null) || activeSession.device_name || `IMEI ${activeSession.imei}`,
          channel: activeSession.channel,
          camera_id: activeSession.channel,
          stream_type: activeSession.stream_type || requestedStreamType,
          is_live: true,
          started_at: activeSession.started_at,
          frames_in: activeSession.frames_in,
          bytes_in: activeSession.bytes_in,
          viewer_count: activeSession.viewer_count,
        };
        setStreams(prev => {
          if (prev.some(p => p.device_id === deviceId && Number(p.camera_id || p.channel) === Number(channel))) return prev;
          return [...prev, attachedStream];
        });
        if (!dev?.always_on_streaming && limits.max_stream_duration_minutes > 0) {
          _setDeadline(deviceId, channel, limits.max_stream_duration_minutes);
        }
        lastActivityRef.current = Date.now();
        setTimeout(() => fetchStreams().catch(() => {}), 1000);
        return;
      }

      // iter149 phase4b — replace AB2/RTMP/WHEP path with native A9A.
      // First fire a stop in case the device still has an in-flight session
      // (idempotent — backend treats 404 / no-session as success).
      try { await api.post('/media/native-capture/stop', { device_id: deviceId, channel, mode: 'live' }); await new Promise(r => setTimeout(r, 800)); } catch (e) {}
      await api.post('/media/native-capture/start', {
        device_id: deviceId,
        channel,
        mode: 'live',
        stream_type: requestedStreamType,
        // relay_host omitted — backend resolves it from `MEDIA_RELAY_HOST` env
      });
      setSelectedDeviceId(deviceId);
      rememberLocalStream(deviceId, dev?.imei, channel);
      // iter149 phase4b — render the tile IMMEDIATELY so the user sees a
      // "Connecting…" placeholder while the device's TCP handshake to
      // :34006 lands. The next fetchStreams() poll (or a successful
      // listener session) will replace this optimistic record with the
      // real one.
      const optimistic = {
        id: `native-${dev?.imei || deviceId}-${channel}`,
        device_id: deviceId,
        device_imei: dev?.imei || '',
        device_name: dev?.name || `Device ${deviceId}`,
        channel,
        camera_id: channel,           // iter150.2 — alias for legacy UI bindings (CHundefined, stop button, deadline lookup)
        stream_type: requestedStreamType,
        is_live: true,
        _optimistic: true,
        started_at: Date.now() / 1000,
      };
      setStreams(prev => {
        if (prev.some(p => p.device_id === deviceId && Number(p.camera_id || p.channel) === Number(channel))) return prev;
        return [...prev, optimistic];
      });
      // Start auto-timeout unless device is always-on OR limit is 0 (disabled)
      if (!dev?.always_on_streaming && limits.max_stream_duration_minutes > 0) {
        _setDeadline(deviceId, channel, limits.max_stream_duration_minutes);
      }
      lastActivityRef.current = Date.now();
      // give the device 1 s to open its TCP, then refresh
      setTimeout(() => fetchStreams().catch(() => {}), 1500);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to start stream'); }
    finally { setStartingKey(null); }
  };

  const stopStream = async (deviceId, channel, { silent = false } = {}) => {
    const key = `${deviceId}|${channel}`;
    if (stoppingRef.current.has(key)) return; // already stopping — skip
    stoppingRef.current.add(key);
    setStoppingKeys(prev => new Set(prev).add(key));
    stoppedTombstonesRef.current.set(key, Date.now() + 15_000);
    const removedStreams = streamsRef.current.filter(s =>
      s.device_id === deviceId && Number(s.camera_id || s.channel) === Number(channel));
    const removedStream = removedStreams[0];
    let localViewerRemoved = false;
    const removeLocalViewer = () => {
      if (localViewerRemoved) return;
      localViewerRemoved = true;
      _clearDeadline(deviceId, channel);
      removedStreams.forEach(s => forgetLocalStream(s.device_id, s.device_imei, s.camera_id || s.channel));
      setStreams(prev => prev.filter(s =>
        !(s.device_id === deviceId && Number(s.camera_id || s.channel) === Number(channel))));
    };
    const restoreLocalViewer = () => {
      if (!localViewerRemoved || removedStreams.length === 0) return;
      removedStreams.forEach(s => rememberLocalStream(s.device_id, s.device_imei, s.camera_id || s.channel));
      setStreams(prev => {
        const existing = new Set(prev.map(s => `${s.device_id}|${s.camera_id || s.channel}`));
        return [...prev, ...removedStreams.filter(s => !existing.has(`${s.device_id}|${s.camera_id || s.channel}`))];
      });
    };
    try {
      const normaliseImei = (value) => String(value || '').replace(/^0+/, '');
      const targetImei = removedStream?.device_imei || deviceById(deviceId)?.imei;
      const activeRes = await api.get('/media/native-live/active-sessions');
      const activeSession = (activeRes.data?.sessions || []).find(session => (
        normaliseImei(session.imei) === normaliseImei(targetImei)
        && Number(session.channel) === Number(channel)
      ));
      if (activeSession && Number(activeSession.viewer_count || 0) > 1) {
        removeLocalViewer();
        if (!silent) toast.success('Closed this viewer');
        return 'viewer_closed';
      }
      removeLocalViewer();
      // iter149 phase4b — A9B replaces the AB3/RTMP teardown path.
      await api.post('/media/native-capture/stop', { device_id: deviceId, channel, mode: 'live' });
      if (!silent) toast.success('Stream stopped');
      return 'stream_stopped';
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        removeLocalViewer();
        if (!silent) toast.success('Stream stopped');
        return 'stream_stopped';
      } else if (!silent) {
        stoppedTombstonesRef.current.delete(key);
        restoreLocalViewer();
        toast.error('Failed to stop stream');
      }
      return 'stop_failed';
    } finally {
      stoppingRef.current.delete(key);
      setStoppingKeys(prev => {
        const next = new Set(prev); next.delete(key); return next;
      });
      setTimeout(() => fetchStreams().catch(() => {}), 2500);
    }
  };

  // Extend stream duration (resets deadline)
  const extendStream = useCallback((deviceId, channel) => {
    _setDeadline(deviceId, channel, limits.max_stream_duration_minutes);
    lastActivityRef.current = Date.now();
    toast.success(`Stream extended ${limits.max_stream_duration_minutes} more minutes`);
  }, [limits]);

  // Auto-timeout runner: checks deadlines + inactivity every tick
  useEffect(() => {
    const inactivityMs = (limits.stream_inactivity_timeout_minutes || 0) * 60_000;
    const idle = Date.now() - lastActivityRef.current > inactivityMs;
    const tabHidden = document.hidden;

    liveStreams.forEach((stream) => {
      const deviceId = stream.device_id;
      const channel = Number(stream.camera_id || stream.channel);
      const key = `${deviceId}|${channel}`;
      const device = deviceById(deviceId);
      if (device?.always_on_streaming || stoppingRef.current.has(key)) return;

      if (limits.max_stream_duration_minutes > 0 && !streamDeadlines[key]) {
        _setDeadline(deviceId, channel, limits.max_stream_duration_minutes);
      }

      const deadline = streamDeadlines[key];
      if (deadline && nowTick > deadline) {
        // Clear deadline FIRST so the tick doesn't re-fire while the API call is in flight
        _clearDeadline(deviceId, channel);
        stopStream(deviceId, channel, { silent: true }).then((result) => {
          if (result === 'viewer_closed') toast.info('This viewer closed (time limit reached)');
          else if (result === 'stream_stopped') toast.info('Stream auto-stopped (time limit reached)');
        });
      } else if (inactivityMs > 0 && tabHidden && idle) {
        _clearDeadline(deviceId, channel);
        stopStream(deviceId, channel, { silent: true }).then((result) => {
          if (result === 'viewer_closed') toast.info('This viewer closed (inactive)');
          else if (result === 'stream_stopped') toast.info('Stream auto-stopped (inactive)');
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowTick]);

  const startIntercom = async (device) => {
    if (intercom.activeDeviceId && intercom.activeDeviceId !== device.id) {
      toast.warning('Stop the current intercom session before opening another');
      return;
    }
    try {
      setSelectedDeviceId(device.id);
      await api.post('/media/native-capture/start', {
        device_id: device.id,
        channel: 129,
        relay_port: 34004,
        mode: 'intercom',
      });
      intercom.startNativeCommand(device.id);
      const tokenRes = await api.post('/media/native-intercom/token', { device_id: device.id });
      const intercomToken = tokenRes.data?.intercom_token;
      if (!intercomToken) throw new Error('Intercom token missing');
      const wsBase = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
      const wsUrl = `${wsBase}/api/media/native-intercom/${device.id}/audio?intercom_token=${encodeURIComponent(intercomToken)}`;
      await intercom.startNativeAudio(device.id, wsUrl);
      toast.success(`Intercom audio active for ${deviceDisplayName(device)}`);
    } catch (e) {
      intercom.stop();
      toast.error(e.response?.data?.detail || e.message || 'Intercom failed to start');
    }
  };

  const stopIntercom = async (deviceId) => {
    try { await api.post('/media/native-capture/stop', { device_id: deviceId, channel: 129, mode: 'intercom' }); } catch (_) {}
    intercom.stop();
    toast.success('Intercom stopped');
  };

  const liveStreams = streams.filter(s => s.is_live);
  const visibleStreams = liveStreams;
  const activeStreamDeviceIds = useMemo(() => new Set(liveStreams.map(s => s.device_id)), [liveStreams]);
  const deviceFilterCounts = useMemo(() => ({
    all: devices.length,
    online: devices.filter(d => d.is_online).length,
    offline: devices.filter(d => !d.is_online).length,
    streaming: devices.filter(d => activeStreamDeviceIds.has(d.id)).length,
  }), [devices, activeStreamDeviceIds]);
  const filteredDevices = useMemo(() => {
    const query = deviceSearchQuery.trim().toLowerCase();
    return [...devices]
      .sort((a, b) => deviceDisplayName(a).localeCompare(deviceDisplayName(b), undefined, { sensitivity: 'base' }))
      .filter(device => {
        const channelNames = Object.values(device.channel_names || {}).join(' ');
        const matchesSearch = !query || [deviceDisplayName(device), device.name, device.imei, channelNames]
          .some(value => String(value || '').toLowerCase().includes(query));
        const isStreaming = activeStreamDeviceIds.has(device.id);
        const matchesFilter = deviceStatusFilter === 'all'
          || (deviceStatusFilter === 'online' && device.is_online)
          || (deviceStatusFilter === 'offline' && !device.is_online)
          || (deviceStatusFilter === 'streaming' && isStreaming);
        return matchesSearch && matchesFilter;
      });
  }, [devices, activeStreamDeviceIds, deviceSearchQuery, deviceStatusFilter]);
  const deviceFilters = [
    { key: 'all', label: 'All', count: deviceFilterCounts.all },
    { key: 'online', label: 'Online', count: deviceFilterCounts.online },
    { key: 'offline', label: 'Offline', count: deviceFilterCounts.offline },
    { key: 'streaming', label: 'Streaming', count: deviceFilterCounts.streaming },
  ];

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#2563eb]" /></div>;

  return (
    <div className="space-y-4" data-testid="live-videos-tab">
      {/* Hidden <audio> for intercom downstream (driver's cabin voice) */}
      <audio ref={intercom.downAudioRef} autoPlay playsInline className="hidden" />

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2"><Play className="w-5 h-5 text-foreground" /><h2 className="text-xl font-semibold">Live Videos</h2></div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            Real-time video streams with sub-second latency
            <span className="ml-1" data-testid="stream-limits-notice">
              {limits.max_stream_duration_minutes > 0 || limits.stream_inactivity_timeout_minutes > 0 ? ' · ' : ''}
              {limits.max_stream_duration_minutes > 0 && (
                <>Streams auto-stop after <strong>{limits.max_stream_duration_minutes} min</strong></>
              )}
              {limits.max_stream_duration_minutes > 0 && limits.stream_inactivity_timeout_minutes > 0 && ' or '}
              {limits.stream_inactivity_timeout_minutes > 0 && (
                <><strong>{limits.stream_inactivity_timeout_minutes} min</strong> inactivity</>
              )}
              {limits.max_stream_duration_minutes === 0 && limits.stream_inactivity_timeout_minutes === 0 && (
                <span className="text-purple-600 dark:text-purple-400"> · No auto-stop (admin disabled)</span>
              )}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { fetchDevices(); fetchStreams(); }} className="gap-1" data-testid="live-refresh">
            <RefreshCw className="w-3 h-3" /> Refresh
          </Button>
        </div>
      </div>

      {/* ── MOBILE-ONLY (under lg breakpoint): tap a device → open Live Tracking style modal ── */}
      <div className="lg:hidden space-y-3" data-testid="live-video-mobile-pane">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search devices..."
            value={deviceSearchQuery}
            onChange={(e) => setDeviceSearchQuery(e.target.value)}
            className="pl-9 pr-9 h-8 text-base"
            data-testid="live-video-mobile-device-search"
          />
          {deviceSearchQuery && (
            <button
              type="button"
              onClick={() => setDeviceSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear device search"
              data-testid="live-video-mobile-device-search-clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1" data-testid="live-video-mobile-filter-bar">
          {deviceFilters.map((filter) => (
            <button
              key={filter.key}
              type="button"
              onClick={() => setDeviceStatusFilter(filter.key)}
              data-testid={`live-video-mobile-filter-${filter.key}`}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                deviceStatusFilter === filter.key
                  ? 'bg-[#2563eb] text-white'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {filter.label} ({filter.count})
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
          <span data-testid="live-video-mobile-device-count">Devices ({filteredDevices.length}/{devices.length})</span>
          {intercom.activeDeviceId && (
            <Badge className="bg-green-500 text-white animate-pulse" data-testid="live-video-mobile-intercom-badge">
              <Mic className="w-3 h-3 mr-1" />
              {intercom.mode === 'ptt'
                ? (intercom.isTransmitting ? 'PTT Transmitting' : 'Intercom Listening')
                : (intercom.isTransmitting ? 'Live Mic Active' : 'Intercom Connecting')}
            </Badge>
          )}
        </div>
        {devices.length === 0 ? (
          <Card data-testid="live-video-mobile-empty">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Camera className="w-12 h-12 text-muted-foreground mb-3 opacity-50" />
              <p className="text-sm font-medium text-muted-foreground">No video devices</p>
              <p className="text-xs text-muted-foreground text-center mt-1">Enable video in Device Management</p>
            </CardContent>
          </Card>
        ) : filteredDevices.length === 0 ? (
          <Card data-testid="live-video-mobile-no-match">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Search className="w-10 h-10 text-muted-foreground mb-3 opacity-50" />
              <p className="text-sm font-medium text-muted-foreground">No matching devices</p>
              <p className="text-xs text-muted-foreground text-center mt-1">Adjust search or filters</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="live-video-mobile-device-list">
            {filteredDevices.map((device) => {
              const activeForDevice = liveStreams.filter((s) => s.device_id === device.id);
              const cameraState = getCameraRecordingState(device);
              const isOnline = cameraState.isGprsOnline;
              const anyRecording = cameraState.hasAnyRecording;
              const isStreaming = activeForDevice.length > 0;
              const channelCount = getConfiguredCameraCount(device);
              return (
                <button
                  key={device.id}
                  type="button"
                  onClick={() => setMobileSelectedDevice(device)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-border bg-card hover:bg-accent transition-colors text-left active:scale-[0.99]"
                  data-testid={`live-video-mobile-device-${device.id}`}
                >
                  <span
                    className={`shrink-0 w-3 h-3 rounded-full ${isOnline ? 'bg-emerald-500' : 'bg-slate-400'} ${anyRecording ? 'ring-2 ring-red-600 dark:ring-red-400 ring-offset-2 ring-offset-card' : ''}`}
                    title={anyRecording ? 'Camera recording' : (isOnline ? 'GPRS online' : 'GPRS offline')}
                    data-testid={`live-video-mobile-device-status-${device.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-foreground truncate">{deviceDisplayName(device)}</div>
                    <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                      <span>{isOnline ? 'Online' : 'Offline'}</span>
                      <span>·</span>
                      <span>{channelCount} {channelCount === 1 ? 'channel' : 'channels'}</span>
                      {anyRecording && (
                        <>
                          <span>·</span>
                          <span className="text-red-600 dark:text-red-400 font-medium">Recording</span>
                        </>
                      )}
                      {isStreaming && (
                        <>
                          <span>·</span>
                          <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0 h-4">
                            <Radio className="w-2.5 h-2.5 mr-0.5" />
                            {activeForDevice.length} live
                          </Badge>
                        </>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="shrink-0 w-5 h-5 text-muted-foreground" />
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── DESKTOP-ONLY (lg+): existing split layout (device list + video grid) ── */}
      <div className="hidden lg:flex gap-4">
        {/* ── LEFT: Device list ── */}
        <div className="w-[280px] shrink-0 bg-card rounded-lg border border-border overflow-hidden max-h-[calc(100vh-220px)] flex flex-col" data-testid="live-video-device-list-panel">
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                placeholder="Search devices..."
                value={deviceSearchQuery}
                onChange={(e) => setDeviceSearchQuery(e.target.value)}
                className="pl-7 pr-7 h-7 text-xs"
                data-testid="live-video-device-search"
              />
              {deviceSearchQuery && (
                <button
                  type="button"
                  onClick={() => setDeviceSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="live-video-device-search-clear"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          <div className="px-2 py-1.5 border-b border-border">
            <div className="flex items-center gap-1 flex-wrap">
              {deviceFilters.map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  onClick={() => setDeviceStatusFilter(filter.key)}
                  data-testid={`live-video-filter-${filter.key}`}
                  className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                    deviceStatusFilter === filter.key
                      ? 'bg-[#2563eb] text-white'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {filter.label} ({filter.count})
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between px-2 py-1.5 border-b border-border bg-background/60">
            <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
              Devices ({filteredDevices.length}/{devices.length})
            </h3>
            {selectedDeviceId && (
              <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px]" onClick={() => setSelectedDeviceId(null)} data-testid="clear-device-filter">
                <X className="w-3 h-3 mr-1" /> Clear
              </Button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
          {devices.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-8">
                <Camera className="w-10 h-10 text-muted-foreground mb-2 opacity-50" />
                <p className="text-xs font-medium text-muted-foreground">No video devices</p>
                <p className="text-[10px] text-muted-foreground text-center mt-1">Enable video in Device Management</p>
              </CardContent>
            </Card>
          ) : filteredDevices.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-8">
                <Search className="w-8 h-8 text-muted-foreground mb-2 opacity-50" />
                <p className="text-xs font-medium text-muted-foreground">No matching devices</p>
                <p className="text-[10px] text-muted-foreground text-center mt-1">Adjust search or filters</p>
              </CardContent>
            </Card>
          ) : (
            filteredDevices.map(device => {
              const activeStreams = liveStreams.filter(s => s.device_id === device.id);
              return (
                <LiveVideoDeviceActionCard
                  key={device.id}
                  device={device}
                  activeStreams={activeStreams}
                  startingKey={startingKey}
                  stoppingKeys={stoppingKeys}
                  onPlay={startStream}
                  onStop={stopStream}
                  intercomActive={intercom.activeDeviceId === device.id}
                  onIntercomStart={startIntercom}
                  onIntercomStop={stopIntercom}
                  isSelected={selectedDeviceId === device.id}
                  onSelect={() => setSelectedDeviceId(selectedDeviceId === device.id ? null : device.id)}
                  intercomMode={intercom.getMode(device.id)}
                  onIntercomModeChange={(nextMode) => intercom.setDeviceMode(device.id, nextMode)}
                  isTransmitting={intercom.activeDeviceId === device.id && intercom.isTransmitting}
                  onPttPress={() => intercom.setTransmitting(true)}
                  onPttRelease={() => intercom.setTransmitting(false)}
                  intercomLocked={Boolean(intercom.activeDeviceId && intercom.activeDeviceId !== device.id)}
                  streamType={getDeviceStreamType(device.id)}
                  onStreamTypeChange={(nextType) => updateDeviceStreamType(device.id, nextType)}
                />
              );
            })
          )}
          </div>
        </div>

        {/* ── RIGHT: Video grid ── */}
        <div className="flex-1 min-w-0 space-y-3">
          <div className="flex items-center gap-2 py-2">
            <Radio className="w-4 h-4 text-red-500" />
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              All Active Channels
              <span className="ml-2 text-foreground">({visibleStreams.length})</span>
            </h3>
            {intercom.activeDeviceId && (
              <Badge className="bg-green-500 text-white animate-pulse ml-auto" data-testid="native-intercom-status-badge">
                <Mic className="w-3 h-3 mr-1" />
                {intercom.mode === 'ptt'
                  ? (intercom.isTransmitting ? 'PTT Transmitting' : 'Intercom Listening')
                  : (intercom.isTransmitting ? 'Live Mic Active' : 'Intercom Connecting')}
              </Badge>
            )}
          </div>

          {visibleStreams.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-24">
                <Video className="w-16 h-16 text-muted-foreground mb-4 opacity-50" />
                <p className="text-lg font-medium text-muted-foreground">
                  No active streams
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Select any online device and click a channel to begin
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3" data-testid="live-video-grid">
              {visibleStreams.map(stream => {
                const dev = devices.find(d => d.id === stream.device_id);
                const label = dev?.channel_names?.[String(stream.camera_id)] || `CH${stream.camera_id}`;
                const alwaysOn = !!dev?.always_on_streaming;
                const deadline = streamDeadlines[`${stream.device_id}|${stream.camera_id}`];
                const timeLeft = (alwaysOn || !deadline) ? null : deadline - nowTick;
                return (
                  <VideoTile
                    key={stream.id}
                    stream={stream}
                    channelLabel={label}
                    deviceLabel={dev ? deviceDisplayName(dev) : null}
                    onStop={stopStream}
                    timeLeftMs={timeLeft}
                    onExtend={() => extendStream(stream.device_id, stream.camera_id)}
                    alwaysOn={alwaysOn}
                    isStopping={stoppingKeys.has(`${stream.device_id}|${stream.camera_id}`)}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Mobile-only device viewer modal — only mounted when a device is tapped */}
      {mobileSelectedDevice && (
        <React.Suspense fallback={null}>
          <LiveTrackingVideoModal
            open
            device={mobileSelectedDevice}
            onOpenChange={(next) => { if (!next) setMobileSelectedDevice(null); }}
          />
        </React.Suspense>
      )}
    </div>
  );
};
